import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import SettingsContainer from "@ats/src/components/shared/SettingsContainer";
import Button from "@ats/src/components/shared/Button";
import FormSection from "@ats/src/components/forms/FormSection";
import FormSelect from "@ats/src/components/forms/FormSelect";
import FormLabel from "@ats/src/components/forms/FormLabel";
import Icon from "@ats/src/components/shared/Icon";

import { useToastContext } from "@shared/context/ToastContext";
import { useUpdateJob } from "@shared/queryHooks/useJob";
import { useCurrentSession } from "@ats/src/context/CurrentSessionContext";

interface Props {
  job: any;
  location: any;
  history?: any;
}

function JobDistributionXHiring(props: Props) {
  const { job, location } = props;
  const addToast = useToastContext();
  const { currentOrganization } = useCurrentSession();

  const { mutate: updateJob, isLoading: isLoadingUpdateJob } = useUpdateJob();

  const [xHiringEnabled, setXHiringEnabled] = React.useState(
    job.externalListingsSettings?.xHiringEnabled !== undefined
      ? job.externalListingsSettings.xHiringEnabled
      : true
  );

  const [isDirty, setIsDirty] = React.useState(false);

  const orgXHiringEnabled = currentOrganization?.enableXHiring;

  // Validation helpers
  const locationNotValid = !(job.city?.length > 0 || job.stateRegion?.length > 0 || job.country?.length > 0);
  const strippedDescription = job.description?.replace(/<\/?[^>]*>/g, "");

    console.log("strippedDescription", strippedDescription);
    const descriptionNotValid = strippedDescription?.length < 100 || !strippedDescription;
  const isMissingRequirements = locationNotValid || descriptionNotValid;

  window.logger("%c[JobDistributionXHiring] ", "background: #EFDDEF; color: #1976D2", {
    props,
    xHiringEnabled,
    job,
    orgXHiringEnabled,
  });

  const handleInputChange = (name, value) => {
    setXHiringEnabled(value);
    setIsDirty(true);
  };

  const handleSubmit = () => {
    const updatedSettings = {
      ...(job.externalListingsSettings || {}),
      xHiringEnabled: xHiringEnabled,
    };

    updateJob(
      {
        id: job.id,
        externalListingsSettings: updatedSettings,
      },
      {
        onSuccess: () => {
          setIsDirty(false);
          addToast({
            title: "X Hiring settings updated",
            kind: "success",
          });
        },
        onError: (response: any) => {
          addToast({
            title: "Could not update X Hiring settings",
            kind: "warning",
          });
        },
      },
    );
  };

  const ViewLink = null;

  const Sidebar = (
    <Styled.Sidebar>
      <Styled.SidebarSection>
        <h3>About X Hiring</h3>
        <p>
          X Hiring allows you to automatically distribute your job posts to X (formerly Twitter) to
          reach a wider audience of potential candidates.
        </p>
        <p>
          To configure X Hiring integration settings for your organization, visit{" "}
          <Button
            type="internalLink"
            link={{
              pathname: `/hire/settings/integrations/xhiring`,
              state: { orginalPathname: location.pathname },
            }}
            styleType="text"
          >
            App settings
          </Button>
          .
        </p>
      </Styled.SidebarSection>
    </Styled.Sidebar>
  );

  const BottomBar = (
    <Button
      className="submit-button"
      type="button"
      onClick={handleSubmit}
      loading={isLoadingUpdateJob}
      disabled={isLoadingUpdateJob}
      aria-label="Save changes"
    >
      Save changes
    </Button>
  );

  return (
    <>
      <SettingsContainer
        fillHeight={true}
        title="X Hiring"
        description="Control whether this specific job is distributed to X Hiring. By default, all jobs are distributed when X Hiring integration is enabled for your organization."
        actions={ViewLink}
        sidebar={Sidebar}
        bottomBar={BottomBar}
      >
        {/* <Styled.StatusBadge>
          {orgXHiringEnabled ? (
            <Styled.StatusContent>
              <Icon name="check-circle" />
              <span>The X Hiring integration is enabled organization wide.</span>
            </Styled.StatusContent>
          ) : (
            <>
                <Styled.StatusContent>
                  <Icon name="info" />
                  <p>
                    The X Hiring integration is not enabled for your organization. An admin can manage this in
                    {" "}
                    <Button
                      type="internalLink"
                      link={{
                        pathname: `/hire/settings/integrations/xhiring`,
                        state: { orginalPathname: location.pathname },
                      }}
                      styleType="text"
                    >
                      App settings
                    </Button>
                    .
                  </p>
              </Styled.StatusContent>
            </>
          )}
        </Styled.StatusBadge> */}

        {orgXHiringEnabled && xHiringEnabled && isMissingRequirements && (
          <Styled.Requirements>
            <FormLabel label="Job requirements for X Hiring" />
            <Styled.RequirementInstructions>
              Your job post does not meet the following requirements for X Hiring distribution.
            </Styled.RequirementInstructions>
            <div>
              {locationNotValid && (
                <Styled.Requirement>
                  <Styled.RequirementTitle>
                    <Icon name="alert-circle" />
                    Jobs must have at least one location field specified (city, state, or country)
                  </Styled.RequirementTitle>
                  <Button
                    type="internalLink"
                    link={`/jobs/${job.id}/setup/details`}
                    styleType="text"
                  >
                    Go to job details
                  </Button>
                </Styled.Requirement>
              )}
              {descriptionNotValid && (
                <Styled.Requirement>
                  <Styled.RequirementTitle>
                    <Icon name="alert-circle" />
                    Job description must contain at least 100 characters
                  </Styled.RequirementTitle>
                  <Button
                    type="internalLink"
                    link={`/jobs/${job.id}/setup/description`}
                    styleType="text"
                  >
                    Go to job description
                  </Button>
                </Styled.Requirement>
              )}
            </div>
          </Styled.Requirements>
        )}

        <FormSection title="Distribution settings">
          <FormSelect
            onChange={handleInputChange}
            name="xHiringEnabled"
            label="X Hiring distribution"
            description="Enable or disable distribution of this job post to X Hiring. This setting is enabled by default."
            value={xHiringEnabled}
            options={[
              { label: "Disabled", value: false },
              { label: "Enabled", value: true },
            ]}
            isDisabled={!orgXHiringEnabled}
          />
        </FormSection>
      </SettingsContainer>
    </>
  );
}

export default JobDistributionXHiring;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Sidebar = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: JobDistributionXHiring_Sidebar;
    position: sticky;
    top: ${t.spacing[4]};
    h3 {
      ${[t.mt(1), t.pt("px"), t.text.base, t.text.bold]};
      color: ${t.dark ? t.color.gray[300] : t.color.black};
    }
    p {
      ${[t.text.xs, t.mt(3)]};
      color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    }
    b {
      ${[t.text.bold]};
      color: ${t.dark ? t.color.gray[300] : t.color.black};
    }
  `;
});

Styled.SidebarSection = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: JobDistributionXHiring_SidebarSection;
    ${t.px(4)};
  `;
});

Styled.Requirements = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: JobDistributionXHiring_Requirements;
    ${[t.mt(6), t.mb(8)]}
    display: flex;
    flex-direction: column;
  `;
});

Styled.RequirementInstructions = styled.p((props) => {
  const t: any = props.theme;
  return css`
    label: JobDistributionXHiring_RequirementInstructions;
    ${[t.text.xs, t.mt(-1), t.mb(2)]};
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    line-height: 1.4;
    max-width: 32rem;
  `;
});

Styled.Requirement = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: JobDistributionXHiring_Requirement;
    ${[t.px(4)]}
    min-height: ${t.spacing[12]};
    border: 1px solid ${t.dark ? t.color.gray[800] : t.color.gray[200]};
    background-color: ${t.dark ? t.color.gray[800] : "transparent"};
    border-top: 1px solid ${t.dark ? t.color.gray[700] : "transparent"};
    display: flex;
    justify-content: space-between;
    align-items: center;

    &:first-of-type {
      border-top: 1px solid ${t.dark ? t.color.gray[800] : t.color.gray[200]};
      border-top-left-radius: 0.4375rem;
      border-top-right-radius: 0.4375rem;
    }

    &:last-of-type {
      border-bottom-left-radius: 0.4375rem;
      border-bottom-right-radius: 0.4375rem;
    }
  `;
});

Styled.RequirementTitle = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: JobDistributionXHiring_RequirementTitle;
    ${[t.my(2), t.mr(6)]};
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    display: flex;
    align-items: center;
    line-height: 1.4;

    svg {
      ${[t.mr(2)]};
      color: ${t.dark ? t.color.red[500] : t.color.red[600]};
      flex-shrink: 0;
    }
  `;
});

Styled.StatusBadge = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: JobDistributionXHiring_StatusBadge;
    ${[t.mt(4), t.mb(8), t.px(4)]}
    background-color: ${t.dark ? t.color.gray[800] : t.color.gray[100]};
    border-radius: 7px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: fit-content;
  `;
});

Styled.StatusContent = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: JobDistributionXHiring_StatusContent;
    ${[t.my(2)]};
    color: ${t.dark ? t.color.gray[300] : t.color.gray[600]};
    display: inline-flex;
    align-items: center;
    line-height: 1.6;

    svg {
      ${[t.mr(2)]};
      color: ${t.dark ? t.color.gray[300] : t.color.gray[600]};
      flex-shrink: 0;
      height: 17px;
      width: 17px;
    }
  `;
});
